import { z } from 'zod/v4';
import { FullCompanySchema } from '../../../companies/index.js';
import { EnabledPlatformFeatureSchema } from '../../../platform-features/index.js';

export const UserDetailParamsSchema = z.object({
  id: z.string().min(1),
});

export type UserDetailParams = z.infer<typeof UserDetailParamsSchema>;

const EnabledDevelopmentTagSchema = z.object({
  featureId: z.string(),
  tag: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isEnabled: z.number(),
});

/**
 * Row shape returned by `GET /users/:id` (company-owner-facing, scoped to
 * companies linked to the invoker — distinct from `AdminUserGetResponseSchema`,
 * which is unscoped and admin-only). `looseObject` for the same reason as
 * `AdminUserGetResponseSchema`: the underlying `users` table has ~25 columns
 * and this mirrors the legacy response verbatim.
 */
export const UserDetailResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  /** Every IAM role key the actor holds in their current session company. */
  roleKeys: z.array(z.string()).optional(),
  companyId: z.string().nullable(),
  // Nullable: a companyless invoker reading their own record (or any user
  // with no resolvable company) gets `company: null` rather than a 404 —
  // see UserDetailService.getById.
  company: FullCompanySchema.extend({
    enabledFeatures: z.array(EnabledPlatformFeatureSchema),
    facilityProcessNames: z.string(),
  }).nullable(),
  appFeatures: z.array(z.unknown()),
  preferences: z.record(z.string(), z.unknown()),
  enabledDevelopmentTags: z.array(EnabledDevelopmentTagSchema),
});

export type UserDetailResponse = z.infer<typeof UserDetailResponseSchema>;
